<template>
  <BaseModal ref="modal">
    <UiModalDialog
      class="cs-search-fields-modal"
      dialog-width="40rem"
      dialog-max-height="90vh"
    >
      <header class="jps-modal-head">
        {{ lss('検索項目の編集', 'Search Field Setting') }}
      </header>
      <div class="jps-modal-content">
        <BaseTab :tab-options="tabOptions" v-model="tabIndex" v-if="tabIndex">
          <BaseTabPanel val="common">
            <table class="jps-table preset-table">
              <tbody>
                <tr>
                  <td>{{ lss('項目', 'Field') }}</td>
                  <td>{{ lss('説明', 'Description') }}</td>
                </tr>
              </tbody>
              <tbody>
                <tr v-for="f in commonFields" :key="f.key">
                  <td>
                    <button
                      class="jps-button"
                      @click="selectCommon(f)"
                      :disabled="commonSelected(f)"
                    >
                      {{ ls(f.name) }}
                    </button>
                  </td>
                  <td>{{ ls(f.description) }}</td>
                </tr>
              </tbody>
            </table>
          </BaseTabPanel>
          <BaseTabPanel val="customize">
            <ValidationForm
              class="jps-field"
              :rule="rule"
              :target="fieldValue"
              only="key"
            />
            <div class="jps-message jps-red" v-if="duplicateKey">
              {{ ls(duplicateKey) }}
            </div>
            <ValidationForm
              :rule="rule"
              :target="fieldValue"
              only="name,description,type"
              @valid="titleValid = $event"
            />

            <div class="jps-field field-margin" v-if="fieldSelectable">
              <label class="jps-label">{{
                lss('検索対象項目', 'Target field')
              }}</label>
              <div class="jps-tag-list">
                <!--表示-->
                <UiDbField
                  class="jps-tag jps-dark jps-tag-ricon"
                  v-for="(f, ind) in fieldValue.fields"
                  :field="f"
                  :key="ind"
                  :deletable="true"
                  @delete="removeFromArray(fieldValue.fields, f)"
                />
                <button
                  type="button"
                  class="jps-tag jps-form"
                  @click.stop="selectKeywordFields()"
                >
                  <BaseIcon size="size-5" name="mdi/plus" />
                </button>
              </div>
              <div class="jps-message jps-red" v-if="zeroField">
                {{ ls(zeroField) }}
              </div>
            </div>
            <ValidationForm
              v-if="editableFields.length"
              class="jps-field field-margin"
              :rule="rule"
              :target="fieldValue"
              :only="editableFields"
            />
          </BaseTabPanel>
        </BaseTab>
      </div>
      <template #buttons>
        <button @click="modal.reject()">{{ ls(dapLangGlobal.cancel) }}</button>
        <button @click="confirm()" :disabled="!valid">{{ ls(dapLangGlobal.ok) }}</button>
      </template>
      <UiDbFieldSelectModal ref="dbFieldSelectModal" />
    </UiModalDialog>
  </BaseModal>
</template>

<script lang='ts' setup>
import { dapLangGlobal } from '#imports'
import type { BaseModal, UiDbFieldSelectModal } from '#components'
import type { TabOption } from '~/components/base/BaseTab.vue'

const { ls, lss } = useI18n()
const { toCSField, systemCommonSearchField } = useCsEditor()

interface Props {
  editstate?: CsEditState,
  field?: CustomSearchField
}
const props = withDefaults(defineProps<Props>(),{
})

const modal = ref<InstanceType<typeof BaseModal>>()
const _editstate: Ref<CsEditState> = ref()
const _field: Ref<CustomSearchField> = ref()
const suppressDuplicateKeyWarning = ref(false)
const fieldValue:Ref<CustomSearchField> = ref(null)
const openAwait = async (options?: Props) => {
  suppressDuplicateKeyWarning.value = false
  titleValid.value = false

  setOptionsValue(_editstate, 'editstate', props, options)
  setOptionsValue(_field, 'field', props, options)

  //field valueの初期化
  let f: CustomSearchField = {}
  if (_field.value) {
    f = clone(_field.value)
    tabIndex.value = 'customize'
  } else {
    tabIndex.value = 'common'
  }
  if (!f.key) f.key = 'f' + _editstate.value.cs.searchFields.length
  if (!f.name) f.name = { ja: '', en: '' }
  if (!f.description) f.description = { ja: '', en: '' }
  if (!f.fields) f.fields = []
  if (!f.samples) f.samples = []
  if (!f.type) f.type = 'keyword'
  if (!f.placeHolder) f.placeHolder = { ja: '', en: '' }
  if (!f.codeKey) f.codeKey = null
  if (!f.facetIncludePattern) f.facetIncludePattern = null
  if (!f.hasFacet) f.hasFacet = false
  fieldValue.value = f
  return await modal.value.openAwait()
}
defineExpose({ openAwait })

const rule = clone(CUSTOM_SEARCH_FIELD_RULE)
delete rule.enums[SEARCH_FIELD_TYPE_DEF.id]['c']['tag']
delete rule.enums[SEARCH_FIELD_TYPE_DEF.id]['c']['code']
const commonFields = systemCommonSearchField()

watch(
  () => fieldValue.value?.type,
  (neu, old) => {
    if (neu !== old && old) {
      fieldValue.value.fields = []
    }
  }
)

const tabIndex: Ref<string> = ref()
const tabOptions = computed((): TabOption[] => {
  //TabHeader
  if (_field.value) {
    return [
      { val: 'customize', label: ls({ ja: 'カスタマイズ', en: 'Customize' }) },
    ]
  } else {
    return [
      {
        val: 'common',
        label: ls({ ja: 'よく使う検索項目', en: 'Common Field' }),
      },
      { val: 'customize', label: ls({ ja: 'カスタマイズ', en: 'Customize' }) },
    ]
  }
})

const editableFields = computed(() => {
  const fields = []
  let behavior = CUSTOM_SEARCH_FIELD_BEHAVIORS[fieldValue.value.type]
  if (behavior.keyword) {
    fields.push('placeHolder', 'samples')
  }
  if (behavior.code) {
    fields.push('codeKey')
  }
  if (behavior.facet) {
    fields.push('hasFacet')
    if (fieldValue.value?.hasFacet) {
      fields.push('facetIncludePattern', 'facetSize')
    }
  }
  fields.push('hidden')
  return fields.join(',')
})

const fieldSelectable = computed(() => {
  let behavior = CUSTOM_SEARCH_FIELD_BEHAVIORS[fieldValue.value.type]
  return behavior?.fields
})

const selectCommon =  (f) => {
  modal.value.resolve(toCSField(f))
}

const commonSelected = (ff) => {
  return _editstate.value.cs.searchFields.some((f) => f.key === ff.key)
}

const titleValid = ref(false)

const valid = computed(() => {
  return (
    titleValid.value &&
    !duplicateKey.value &&
    (!fieldSelectable.value || !zeroField.value)
  )
})

const zeroField = computed(() => {
  if (fieldValue.value && fieldValue.value.fields.length === 0) {
    return {
      ja: '検索対象項目が指定されていません。一つ以上の項目を指定してください。',
      en: 'Select one or more fields',
    }
  } else {
    return null
  }
})

const duplicateKey = computed(() => {
  if (suppressDuplicateKeyWarning.value) return null
  if (
    fieldValue.value &&
    _editstate.value.cs.searchFields
      .filter((f) => f !== _field.value && f !== fieldValue.value)
      .some((f) => f.key === fieldValue.value.key)
  ) {
    return {
      ja: '検索項目キーが重複しています。キーを変更してください。',
      en: 'Search field key is duplicated',
    }
  } else {
    return null
  }
})

const confirm =  () => {
  if (valid.value) {
    suppressDuplicateKeyWarning.value = true
    let f: CustomSearchField = normalizeEmpty(fieldValue.value)
    let behavior = CUSTOM_SEARCH_FIELD_BEHAVIORS[fieldValue.value.type]
    if (!behavior.facet) {
      delete f.facetSize
      delete f.facetIncludePattern
      delete f.facetSize
    }
    if (!behavior.code) {
      delete f.codeKey
    }
    if (!behavior.fields) {
      delete f.fields
    }
    if (!behavior.keyword) {
      delete f.placeHolder
      delete f.samples
    }

    modal.value.resolve(f)
  }
}

const dbFieldSelectModal = ref<InstanceType<typeof UiDbFieldSelectModal>>()
const selectKeywordFields = async () => {
  try {
    const keyword = await dbFieldSelectModal.value.openAwait({
      fields: fieldValue.value.fields,
      database: _editstate.value.db
          ? [_editstate.value.db.id]
          : _editstate.value.cs.databaseIds,
      indexType:
        CUSTOM_SEARCH_FIELD_BEHAVIORS[fieldValue.value.type]?.indexType,
    })
    fieldValue.value.fields = keyword
  } catch (error) {}
}
</script>

<style lang="scss">
.cs-search-fields-modal {
  .jps-tag-list {
    margin: 0;
  }
  .field-margin {
    margin-top: 1rem;
  }
  .preset-table {
    button {
      width: 100%;
    }
  }
}
</style>
